import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Search,
  CreditCard,
  Calendar,
  Eye,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      // Get all users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const allTransactions = [];

      // Fetch transactions for each user
      for (const userDoc of usersSnapshot.docs) {
        try {
          const transactionsSnapshot = await getDocs(
            query(
              collection(db, 'wallet_transactions', userDoc.id, 'transactions'),
              orderBy('date', 'desc'),
            ),
          );

          transactionsSnapshot.docs.forEach(doc => {
            allTransactions.push({
              id: doc.id,
              userId: userDoc.id,
              userEmail: userDoc.data().email,
              ...doc.data(),
            });
          });
        } catch (error) {
          console.error(
            `Error fetching transactions for user ${userDoc.id}:`,
            error,
          );
        }
      }

      // Sort by date (most recent first)
      allTransactions.sort((a, b) => {
        const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
        const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
        return dateB - dateA;
      });

      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch =
      transaction.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = filterType === 'all' || transaction.type === filterType;

    return matchesSearch && matchesType;
  });

  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeBadge = type => {
    const typeConfig = {
      deposit: {
        color: 'bg-green-100 text-green-800',
        label: 'Deposit',
        icon: ArrowDown,
      },
      withdrawal: {
        color: 'bg-red-100 text-red-800',
        label: 'Withdrawal',
        icon: ArrowUp,
      },
      payment: {
        color: 'bg-blue-100 text-blue-800',
        label: 'Payment',
        icon: ArrowDown,
      },
      refund: {
        color: 'bg-purple-100 text-purple-800',
        label: 'Refund',
        icon: ArrowUp,
      },
      transfer: {
        color: 'bg-yellow-100 text-yellow-800',
        label: 'Transfer',
        icon: ArrowUp,
      },
    };
    const config = typeConfig[type] || {
      color: 'bg-gray-100 text-gray-800',
      label: type,
      icon: CreditCard,
    };
    const Icon = config.icon;
    return (
      <span
        className={`px-2 py-1 text-xs font-semibold rounded-full ${config.color} flex items-center space-x-1`}
      >
        <Icon size={12} />
        <span>{config.label}</span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
          <p className="text-gray-600 mt-1">
            Total {transactions.length} transactions
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={20}
          />
          <input
            type="text"
            placeholder="Search transactions (user, type, description)..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
          />
        </div>
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
        >
          <option value="all">All Types</option>
          <option value="deposit">Deposit</option>
          <option value="withdrawal">Withdrawal</option>
          <option value="payment">Payment</option>
          <option value="refund">Refund</option>
          <option value="transfer">Transfer</option>
        </select>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTransactions.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {searchTerm || filterType !== 'all'
                      ? 'No transactions found'
                      : 'No transactions yet'}
                  </td>
                </tr>
              ) : (
                filteredTransactions.map(transaction => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {transaction.userEmail ||
                          transaction.userId?.substring(0, 8) + '...'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getTypeBadge(transaction.type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`text-sm font-semibold ${
                          transaction.type === 'deposit' ||
                          transaction.type === 'refund'
                            ? 'text-green-600'
                            : transaction.type === 'withdrawal' ||
                              transaction.type === 'payment'
                            ? 'text-red-600'
                            : 'text-gray-900'
                        }`}
                      >
                        {transaction.type === 'deposit' ||
                        transaction.type === 'refund'
                          ? '+'
                          : '-'}
                        ${Math.abs(transaction.amount || 0).toFixed(2)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {transaction.description || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar size={16} className="mr-2 text-gray-400" />
                        {formatDate(transaction.date)}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
